/**
 * agentMessageWindow — session title helpers.
 *
 * Provides:
 *   deriveTaskSessionTitle  — smart title from first user message
 *   formatSessionListTitle  — "Resume Edit · 25 May" label for sidebar rows
 */

const FILLER_PREFIX = /^(hey|hi|hello|ok|okay|sure|great|can you|could you|please|i need|i want|help me|let's|lets|i'd like|id like|i would like)\s+/i;
const STOP_WORDS = new Set(['a','an','the','and','or','but','in','on','at','to','for','of','with','by','from','up','about','into','through','during','is','are','was','were','be','been','being','have','has','had','do','does','did','will','would','could','should','may','might','shall','need','wants','find','get','me','my','i','it','this','that','these','those']);

/**
 * Converts the first user message into a concise session title (≤ 60 chars).
 * Strips filler phrases and capitalises the result.
 */
export function deriveTaskSessionTitle(firstUserMessage: string): string {
  let msg = firstUserMessage.trim();

  // Remove filler opening phrase
  msg = msg.replace(FILLER_PREFIX, '');

  // Strip trailing punctuation
  msg = msg.replace(/[?!.]+$/, '').trim();

  // Capitalise first char
  if (msg.length > 0) {
    msg = msg.charAt(0).toUpperCase() + msg.slice(1);
  }

  // Truncate to 60 chars at a word boundary
  if (msg.length > 60) {
    const cut = msg.slice(0, 60);
    const lastSpace = cut.lastIndexOf(' ');
    msg = lastSpace > 30 ? cut.slice(0, lastSpace) + '…' : cut + '…';
  }

  return msg || 'New chat';
}

/**
 * Formats a session row title for the sidebar:
 *   - Same day  → "Resume Edit · 3:45 PM"
 *   - This week → "Resume Edit · Mon 25"
 *   - Older     → "Resume Edit · 25 May"
 */
export function formatSessionListTitle(
  title: string | null | undefined,
  updatedAt: string,
): string {
  const clean = (title ?? '').trim();
  if (!clean) return 'New chat';

  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return clean;

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const msDiff = now.getTime() - date.getTime();
  const isThisWeek = msDiff < 7 * 24 * 60 * 60 * 1000;

  let datePart: string;
  if (isToday) {
    datePart = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (isThisWeek) {
    datePart = date.toLocaleDateString([], { weekday: 'short', day: 'numeric' });
  } else {
    datePart = date.toLocaleDateString([], { day: 'numeric', month: 'short' });
  }

  return `${clean} · ${datePart}`;
}
